import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, CalendarDays, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

export default function ConfirmIntake() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [names, setNames] = useState({});
  const [accepted, setAccepted] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("This link is invalid."); setState("error"); return; }
    api.get(`/public/intake?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setData(r.data);
        setState(r.data.completed ? "done" : "ready");
        const n = {}; (r.data.member?.name ? r.data.agreements : []).forEach((a) => { n[a.id] = r.data.member.name; });
        setNames(n);
      })
      .catch((e) => { setError(formatApiError(e.response?.data?.detail) || "This link is invalid or expired."); setState("error"); });
  }, [token]);

  const submit = async () => {
    const signatures = data.agreements.map((a) => ({ agreement_id: a.id, name: (names[a.id] || "").trim(), consent: !!accepted[a.id] }));
    if (signatures.some((s) => !s.consent || s.name.length < 2)) { setError("Please accept and type your name on every agreement."); return; }
    setSubmitting(true); setError("");
    try {
      await api.post("/public/intake/sign", { token, signatures });
      setState("done");
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || "Unable to submit."); }
    finally { setSubmitting(false); }
  };

  if (state === "loading") return <div className="min-h-screen grid place-items-center bg-[#F9F7F3]"><Loader2 className="animate-spin text-[#144F75]" /></div>;
  if (state === "error") return <div className="min-h-screen grid place-items-center bg-[#F9F7F3] px-4"><div className="eyw-card p-8 max-w-md text-center" data-testid="confirm-error"><p className="text-[#1A242B]">{error}</p></div></div>;
  if (state === "done") return (
    <div className="min-h-screen grid place-items-center bg-[#F9F7F3] px-4">
      <div className="eyw-card p-8 max-w-md text-center" data-testid="confirm-done">
        <CheckCircle2 size={40} className="text-[#5C8A72] mx-auto mb-3" />
        <h1 className="font-serif text-2xl text-[#1A242B] mb-1">You're all set!</h1>
        <p className="text-sm text-[#4A5D6B]">Your agreements are signed and your appointment is confirmed. We can't wait to welcome you.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F7F3] py-10 px-4" data-testid="confirm-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="text-center">
          <p className="eyw-label">Enhancing Your World</p>
          <h1 className="font-serif text-3xl text-[#1A242B]">Confirm your appointment</h1>
        </div>
        <div className="eyw-card p-6">
          <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-2"><CalendarDays size={19} className="text-[#144F75]" /> Your visit</h2>
          <p className="text-[#1A242B]">{data.appointment.service_title}</p>
          <p className="text-sm text-[#4A5D6B]">{fmtDate(data.appointment.date)} at {fmtTimeRange(data.appointment.start_time)}{data.appointment.location && ` · ${data.appointment.location}`}</p>
        </div>
        {data.deposit?.amount > 0 && (
          <div className="eyw-card p-6">
            <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><Receipt size={19} className="text-[#144F75]" /> Deposit</h2>
            <p className="text-sm text-[#4A5D6B]">{money(Number(data.deposit.amount))} · {data.deposit.method} · <span className="capitalize">{String(data.deposit.status || "").replace(/_/g, " ")}</span> (applied to your balance at your visit)</p>
          </div>
        )}
        <div className="eyw-card p-6">
          <h2 className="font-serif text-xl text-[#1A242B] mb-3">Review &amp; sign</h2>
          <div className="space-y-4">
            {data.agreements.map((a) => (
              <div key={a.id} className="border border-[#E5E0D8] rounded-2xl p-4" data-testid={`confirm-ag-${a.id}`}>
                <p className="font-semibold text-[#1A242B] mb-1">{a.title}</p>
                <div className="text-xs text-[#4A5D6B] max-h-40 overflow-y-auto whitespace-pre-wrap border-b border-[#E5E0D8] pb-3 mb-3">{a.content || "Please review the terms with our team."}</div>
                <label className="flex items-center gap-2 text-sm text-[#1A242B] mb-2">
                  <input type="checkbox" checked={!!accepted[a.id]} onChange={(e) => setAccepted({ ...accepted, [a.id]: e.target.checked })} data-testid={`confirm-accept-${a.id}`} />
                  I have read and agree
                </label>
                <input placeholder="Type your full name to sign" value={names[a.id] || ""} onChange={(e) => setNames({ ...names, [a.id]: e.target.value })} data-testid={`confirm-sign-${a.id}`} className="eyw-input w-full text-sm" />
              </div>
            ))}
            {data.agreements.length === 0 && <p className="text-sm text-[#4A5D6B]">No agreements pending — tap confirm below.</p>}
          </div>
          {error && <p className="text-sm text-[#B5794E] mt-3">{error}</p>}
          <button onClick={submit} disabled={submitting} data-testid="confirm-submit" className="eyw-btn w-full mt-5 disabled:opacity-60">{submitting && <Loader2 className="animate-spin" size={18} />} Confirm &amp; sign</button>
        </div>
      </div>
    </div>
  );
}
